import i18next from 'i18next';
import { MUTATION_updateCommunitySkills } from 'mutation';
import { QUERY_skillsLocationSettingsPage } from 'queries';
import { toastService } from 'services';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import {
  MutationOutcomeStatusEnum,
  Mutation_UpdateCommunitySkillsMutation,
  Mutation_UpdateCommunitySkillsMutationVariables,
  SkillsLocationSettingsPageQuery,
  SkillsLocationSettingsPageQueryVariables,
} from 'types/graphql';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { create } from 'zustand';
import { getCommunitiesSkillsAttributes } from 'pages/Settings/pages/LocationSettings/SkillsAllocation/SkillsAllocationSettingsPage.constants';
import {
  LocationSkillsData,
  LocationSkillsState,
} from 'pages/Settings/pages/LocationSettings/SkillsAllocation/store/locationSkills.store.types';

const INITIAL_STATE: LocationSkillsData = {
  organizationSkills: [],
  appliedCommunitiesSkills: [],
  selectedSkillIds: [],
  communityRelayId: null,
  isLoading: true,
  isSaving: false,
};

let fetchId = 0;

export const locationSkillsStore = create<LocationSkillsState>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchSkills: (apolloClient, locationId) => {
      const currentFetchId = ++fetchId;
      set({ ...INITIAL_STATE });

      apolloClient
        .query<SkillsLocationSettingsPageQuery, SkillsLocationSettingsPageQueryVariables>({
          query: QUERY_skillsLocationSettingsPage,
          variables: { locationId },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          if (currentFetchId !== fetchId) return;
          const appliedCommunitiesSkills = data.fetchCommunity.communitiesSkills ?? [];
          set({
            organizationSkills: data.fetchCommunity.organization?.skills ?? [],
            appliedCommunitiesSkills,
            selectedSkillIds: appliedCommunitiesSkills.map(({ skill }) => skill.displayId),
            communityRelayId: data.fetchCommunity.relayId,
            isLoading: false,
          });
        })
        .catch((error) => {
          if (currentFetchId !== fetchId) return;
          handleGenericErrorMessage(error);
          set({ isLoading: false });
        });

      return () => {
        fetchId++;
      };
    },

    toggleSkill: (displayId) =>
      set((state) => {
        const skill = state.organizationSkills.find((orgSkill) => orgSkill.displayId === displayId);
        if (skill?.applyToAllLocations) return state;
        return {
          selectedSkillIds: state.selectedSkillIds.includes(displayId)
            ? state.selectedSkillIds.filter((id) => id !== displayId)
            : [...state.selectedSkillIds, displayId],
        };
      }),

    discardChanges: () =>
      set((state) => ({
        selectedSkillIds: state.appliedCommunitiesSkills.map(({ skill }) => skill.displayId),
      })),

    saveSkills: async (apolloClient) => {
      const { appliedCommunitiesSkills, selectedSkillIds, communityRelayId } = get();
      if (!communityRelayId) return false;

      const communitiesSkillsAttributes = getCommunitiesSkillsAttributes(
        appliedCommunitiesSkills,
        selectedSkillIds,
      );
      const saveFetchId = fetchId;
      set({ isSaving: true });

      try {
        const { data } = await apolloClient.mutate<
          Mutation_UpdateCommunitySkillsMutation,
          Mutation_UpdateCommunitySkillsMutationVariables
        >({
          mutation: MUTATION_updateCommunitySkills,
          variables: {
            communityId: communityRelayId,
            communitiesSkillsAttributes,
          },
        });

        if (saveFetchId !== fetchId) return false;

        const payload = data?.updateCommunity;
        if (!payload || payload.errors?.length || payload.status !== MutationOutcomeStatusEnum.ok) {
          toastService.error(
            payload?.errors?.join(' ') || i18next.t('errors:SOMETHING_WENT_WRONG'),
          );
          const currentCommunitiesSkills =
            payload?.community?.communitiesSkills ?? appliedCommunitiesSkills;
          set({
            appliedCommunitiesSkills: currentCommunitiesSkills,
            selectedSkillIds: currentCommunitiesSkills.map(({ skill }) => skill.displayId),
            isSaving: false,
          });
          return false;
        }

        const nextCommunitiesSkills = payload.community.communitiesSkills ?? [];
        set({
          appliedCommunitiesSkills: nextCommunitiesSkills,
          selectedSkillIds: nextCommunitiesSkills.map(({ skill }) => skill.displayId),
          isSaving: false,
        });
        toastService.success(i18next.t('success:LOCATION_SKILLS_SAVED'));
        return true;
      } catch (error) {
        handleGenericErrorMessage(error);
        if (saveFetchId === fetchId) set({ isSaving: false });
        return false;
      }
    },

    reset: () => set(INITIAL_STATE),
  })),
);

export const useLocationSkillsStore = locationSkillsStore;
